import { FC, PropsWithChildren, useEffect } from 'react';
import { AnimatePresence } from 'framer-motion';

import {
    DUPLICATE_FLOW_MODAL_ID,
    PLAYER_TYPE_MODAL_ID,
    PREFLIGHT_CHECKS_MODAL_ID,
    PREFLIGHT_FAILURE_MODAL_ID,
    RENAME_MODAL_ID,
    CONFIRM_PASSWORD_MODAL_ID,
    CREATE_REEL_MODAL_ID,
    TRIAL_EXTEND_MODAL_ID,
    AI_VIDEO_LABEL_MODAL_ID,
    PREVIEW_MODAL_ID,
    DELETE_FLOW_MODAL_ID,
    UNPUBLISH_FLOW_MODAL_ID,
} from '@/types/modal.type';
import { DeleteFlowModal } from '@/Components/modal/DeleteFlowModal';
import { DuplicateFlowModal } from '@/Components/modal/DuplicateFlowModal';
import { PlayerTypeModal } from '@/Components/modal/PlayerTypeModal';
import { PreflightChecksModal } from '@/Components/modal/PreflightChecksModal';
import { PreflightFailureModal } from '@/Components/modal/PreflightFailureModal';
import { FlowRenameModal } from '@/Components/modal/FlowRenameModal';
import { ConfirmPasswordModal } from '@/Components/ConfirmPasswordModal';
import { CreateReelModal } from '@/Components/modal/CreateReelModal';
import { TrialExtendModal } from '@/Components/modal/TrialExtendModal';
import { AiVideoLabelModal } from '@/Components/modal/AiVideoLabelModal';
import { useInteractiveCallback } from '@/hook/useInteractiveCallback';
import { FlowPreviewModal } from '@/Components/modal/FlowPreviewModal';

import { useAppModal } from './AppModal.context';
import { UnpublishFlowModal } from '@/Components/modal/UnpublishFlowModal';

type AppModalProps = PropsWithChildren<{}>;

export const AppModal: FC<AppModalProps> = ({}) => {
    const { dialogElementRef, modalData, setModalData, setDialogElementRef } =
        useAppModal();

    useEffect(() => {
        if (dialogElementRef?.current) {
            dialogElementRef.current.showModal();
        }
    }, [dialogElementRef]);

    const modalId = modalData?.id;

    const modals = [
        // TODO: Remove unneeded modals
        // {
        //     id: APPEND_DICTIONARY_MODAL_ID,
        //     Component: AppendDictionaryModal,
        // },
        {
            id: DELETE_FLOW_MODAL_ID,
            Component: DeleteFlowModal,
        },
        {
            id: UNPUBLISH_FLOW_MODAL_ID,
            Component: UnpublishFlowModal,
        },
        {
            id: DUPLICATE_FLOW_MODAL_ID,
            Component: DuplicateFlowModal,
        },
        {
            id: PLAYER_TYPE_MODAL_ID,
            Component: PlayerTypeModal,
        },
        {
            id: PREFLIGHT_CHECKS_MODAL_ID,
            Component: PreflightChecksModal,
        },
        {
            id: PREFLIGHT_FAILURE_MODAL_ID,
            Component: PreflightFailureModal,
        },
        {
            id: RENAME_MODAL_ID,
            Component: FlowRenameModal,
        },
        {
            id: CONFIRM_PASSWORD_MODAL_ID,
            Component: ConfirmPasswordModal,
        },
        {
            id: CREATE_REEL_MODAL_ID,
            Component: CreateReelModal,
        },
        {
            id: TRIAL_EXTEND_MODAL_ID,
            Component: TrialExtendModal,
        },
        {
            id: AI_VIDEO_LABEL_MODAL_ID,
            Component: AiVideoLabelModal,
        },
        {
            id: PREVIEW_MODAL_ID,
            Component: FlowPreviewModal,
        },
    ];

    const onClose = useInteractiveCallback(() => {
        setModalData(null);

        dialogElementRef?.current?.close();

        setDialogElementRef(null);
    });

    return (
        <AnimatePresence>
            {modals.map(({ id, Component }) => {
                if (id === modalId) {
                    return (
                        <Component
                            key={id}
                            payload={modalData?.payload || null}
                            onClose={onClose}
                        />
                    );
                }

                return null;
            })}
        </AnimatePresence>
    );
};
